import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { AuthService } from './core/services/auth.service';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { User } from './models/user.interface';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from './core/services/theme.service';
import { SidebarService } from './core/services/sidebar.service';
import { LoadingService } from './core/services/loading.service';
import { LoadingScreenComponent } from './shared/components/loading-screen/loading-screen.component';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, SidebarComponent, LoadingScreenComponent],
  templateUrl: './app.html',
  styleUrls: ['./app.css']
})
export class App implements OnDestroy {
  currentUser$: Observable<User | null>;
  isLoading$: Observable<boolean>;
  wrapperClasses$: Observable<any>;
  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private themeService: ThemeService,
    public sidebarService: SidebarService,
    public loadingService: LoadingService,
    private router: Router
  ) {
    this.translate.setDefaultLang('en');
    this.currentUser$ = this.authService.currentUser$;
    this.isLoading$ = this.loadingService.isLoading$;

    const isLoginPage$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects === '/login'),
      startWith(this.router.url === '/login') // Check initial URL
    );

    this.wrapperClasses$ = combineLatest([
      isLoginPage$,
      this.sidebarService.isOpen$
    ]).pipe(
      map(([isLogin, isSidebarOpen]) => ({
        'login-page': isLogin,
        'sidebar-closed': !isSidebarOpen && !isLogin
      }))
    );

    // Gestiona los ajustes del usuario
    this.subscriptions.add(
      this.currentUser$.subscribe(user => {
        if (user) {
          this.sidebarService.open(); // Open sidebar on login
          this.translate.use(user.settings?.language || 'en');
          this.themeService.setTheme(user.settings?.theme || 'light');
        } else {
          this.sidebarService.close(); // Close sidebar on logout
          this.translate.use('en');
          this.themeService.setTheme('light');
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}

